/**
 * Sync service for local-first architecture
 * Handles syncing between IndexedDB and Cloudflare backend
 */

import * as localDB from './localDB';
import { getUserIdFromJWT } from './utils';
import { reportError } from './error-reporter';
import { apiHeaders } from './api/helpers';

import { API_BASE } from './config.js';

/**
 * Merge server progress with local data to preserve fields D1 doesn't store
 * (scrollTop, scrollHeight) and respect local-first writes.
 *
 * If a local record has unsaved changes (_pending) or a newer updated_at,
 * keep local values and only take server-only fields (scrollTop/scrollHeight
 * are already local). This prevents sync from overwriting a position the
 * user just saved but that hasn't been uploaded yet.
 */
/**
 * Pick the greater of two server-clock ISO-8601 timestamps. Lexicographic
 * comparison == chronological for full `new Date().toISOString()` strings
 * (same fixed-width format, always ends in 'Z'). Used to advance the per-row
 * `server_updated_at` high-water-mark monotonically.
 */
function maxIso(a, b) {
    if (!a) return b || null;
    if (!b) return a;
    return a > b ? a : b;
}

// Earliest non-null wins. Used to pin finished_at to the FIRST finish across a
// local/server merge, independent of the updated_at tiebreak: a re-mark bumps
// updated_at to now and would otherwise spread its LATER finished_at back into
// IDB and re-push it. (ISO lexicographic '<' is chronological for Z-strings.)
function minIso(a, b) {
    if (!a) return b || null;
    if (!b) return a;
    return a < b ? a : b;
}

function mergeProgressWithLocal(serverProgress, localMap) {
    if (!localMap || localMap.size === 0) {
        return serverProgress.map(prog => {
            const id = prog.id || ((prog.user_id && prog.book_id) ? `${prog.user_id}:${prog.book_id}` : prog.id);
            // Adopt the server row and seed the server-clock high-water-mark.
            return { ...prog, id, server_updated_at: prog.updated_at || null };
        });
    }
    return serverProgress.map(prog => {
        const key = `${prog.user_id}:${prog.book_id}`;
        const local = localMap.get(key) || localMap.get(prog.id);
        if (!local) {
            // No local match — adopt the server row and seed the HWM.
            const id = prog.id || ((prog.user_id && prog.book_id) ? `${prog.user_id}:${prog.book_id}` : prog.id);
            return { ...prog, id, server_updated_at: prog.updated_at || null };
        }
        // Local wins if it has unsynced edits (_pending) OR its server-clock
        // high-water-mark is at/after the incoming server row.
        //
        // CLOCK-SKEW SAFETY: the tiebreak compares SERVER-clock values on both
        // sides — incoming `prog.updated_at` (server-stamped, sync.ts:525 /
        // progress.ts) against `local.server_updated_at` (the server-stamped
        // value this row last reflected). It NEVER uses `local.updated_at`,
        // which is the CLIENT wall clock (progressStore.updateProgress → now()).
        // That stops a device with a fast/slow clock from making a stale local
        // row look "newer" and shadowing a genuinely newer position from
        // another device. Legacy local rows that predate this field have no
        // server_updated_at → the server wins, which is always safe: a
        // non-_pending local position is, by construction, <= the server's
        // (it was either pulled from the server or already pushed to it).
        const localWins = local._pending
            || (local.server_updated_at && prog.updated_at && local.server_updated_at > prog.updated_at);
        if (localWins) {
            // Preserve local cfi/percentage/chapter (the position data) but
            // merge any server-only metadata the local record doesn't have.
            const merged = { ...prog, ...local };
            // Defense: when this device hadn't synced since the book was marked
            // finished (local finished_at=null but server has it), adopt the
            // server's finish POSITION (higher percentage/cfi). The finished_at
            // VALUE itself is resolved unconditionally below by minIso.
            if (prog.finished_at && !local.finished_at) {
                if ((prog.percentage || 0) > (local.percentage || 0)) {
                    merged.percentage = prog.percentage;
                    merged.cfi = prog.cfi;
                    merged.chapter = prog.chapter;
                    merged.page = prog.page;
                }
            }
            // finished_at FIRST-WRITE-WINS: earliest non-null, regardless of which
            // row won the updated_at tiebreak above. Without this, a re-mark's
            // later finished_at (spread in via {...local}) would propagate to IDB
            // and re-push to the server, undoing the backend's earliest-wins guard.
            merged.finished_at = minIso(local.finished_at, prog.finished_at);
            // Ensure merged result has id for IndexedDB keyPath
            if (!merged.id && prog.user_id && prog.book_id) {
                merged.id = `${prog.user_id}:${prog.book_id}`;
            }
            // Advance the high-water-mark to the newest server-clock value seen
            // (so once a _pending edit is pushed + cleared, the next merge
            // compares against an up-to-date server baseline).
            merged.server_updated_at = maxIso(local.server_updated_at, prog.updated_at);
            return merged;
        }
        // Server is newer — take server values, preserve local-only fields.
        // CRITICAL: always keep local.id to avoid ConstraintError on the
        // user_book unique index (local may have UUID id while server has
        // composite id — using server id would create a second record with
        // the same [user_id, book_id], violating the unique index).
        const result = { ...local, ...prog, id: local.id || prog.id };
        if (!result.id && prog.user_id && prog.book_id) {
            result.id = `${prog.user_id}:${prog.book_id}`;
        }
        // finished_at FIRST-WRITE-WINS even when the server row wins the merge:
        // a local first-finish must not be overwritten by a server row whose
        // finished_at drifted later (e.g. an old client re-marked it).
        result.finished_at = minIso(local.finished_at, prog.finished_at);
        result.server_updated_at = maxIso(local.server_updated_at, prog.updated_at);
        return result;
    });
}

let syncInProgress = false;
let activeSyncPromise = null;
let syncTimeout = null;
/** Stored reference to the 'online' listener so it can be removed on stop */
let onlineListener = null;
/** Stored reference to the 'visibilitychange' listener so it can be removed on stop */
let visibilityListener = null;
/** Called when sync returns 401 (e.g. token invalid / other device); set by AuthContext */
let onUnauthorizedCallback = null;
/** Subscribers notified after each successful sync so a view can refresh cloud
 *  data. A Set (not a single slot) so multiple views — the Library shelf AND an
 *  open reader — can subscribe without clobbering each other. */
const syncCompleteCallbacks = new Set();
/** Only paid users get cloud sync; free users use local-only */
let syncEnabled = false;
/**
 * True once stopPeriodicSync() ran; doSync checks it before re-arming its
 * 30s setTimeout. Without this, stopping DURING a tick (401/403 handling
 * runs while doSync is awaiting performSync) clears the already-fired
 * timeout id and the loop resurrects itself right after.
 */
let periodicStopped = true;
/**
 * One-shot latch so a server 403 ("sync not available for this plan scope")
 * reports at most ONCE per enable cycle instead of every 30s tick.
 * Reset when sync is (re-)enabled — e.g. the user upgraded / re-logged-in.
 */
let sync403Reported = false;

export function setSyncEnabled(enabled) {
    syncEnabled = !!enabled;
    if (syncEnabled) sync403Reported = false;
}

/**
 * Optional background-backup runner, driven by the same 30s sync loop. Set by
 * AuthContext to `() => api.runCloudBackupScan()` for paid users with
 * auto-backup on. Kept as a registered callback (not a direct import) to avoid
 * a syncService ↔ bookManager circular dependency. null = no-op.
 */
let cloudBackupRunner = null;
export function setCloudBackupRunner(fn) {
    cloudBackupRunner = typeof fn === 'function' ? fn : null;
}

/** Whether cloud sync is active (paid user). Read by the pull-on-open path. */
export function isSyncEnabled() {
    return syncEnabled;
}

/**
 * Subscribe to "sync completed" notifications. Returns an unsubscribe function.
 * The callback receives `{ progress }` — the merged progress rows written to
 * IndexedDB in this sync — so a subscriber (e.g. an open reader) can react to a
 * cross-device position change without racing the IDB write. Passing `null`
 * clears ALL subscribers (legacy teardown; new callers should prefer the
 * returned unsubscribe).
 */
export function onSyncComplete(callback) {
    if (callback == null) {
        syncCompleteCallbacks.clear();
        return () => {};
    }
    syncCompleteCallbacks.add(callback);
    return () => syncCompleteCallbacks.delete(callback);
}

/**
 * Initialize sync service (paid users only)
 * Call this after user logs in with a paid plan (basic or pro)
 * @param {string} token
 * @param {(message?: string) => void} [onUnauthorized] - called when server returns 401
 */
export async function initSync(token, onUnauthorized) {
    if (!token || !syncEnabled) return;
    onUnauthorizedCallback = onUnauthorized || null;

    // First-ever sync (lastSyncTime is null) will naturally be a full sync.
    // Subsequent launches use incremental sync (only changes since last sync).
    await performSync(token);
    // The first sync may have hit a terminal 403 (sync not available for this
    // plan scope) which disables sync — don't start the 30s loop in that case.
    if (!syncEnabled) return;
    startPeriodicSync(token);
}

/**
 * Start periodic sync
 *
 * Skips sync when the page is hidden (user switched apps / locked screen) —
 * Android Chrome doesn't fully suspend background JS, so without this guard
 * the 30s setTimeout keeps firing API calls and draining battery + mobile
 * data even though the user isn't using Reader. visibilitychange listener
 * triggers an immediate sync when the user returns.
 */
function startPeriodicSync(token) {
    stopPeriodicSync();
    periodicStopped = false;

    const doSync = async () => {
        if (!document.hidden) {
            await performSync(token);
            // After metadata sync, opportunistically back up one local-only
            // book to the cloud (paid + auto-backup on). Best-effort; throttled
            // to one book per tick inside the runner. Gated on syncEnabled so
            // a terminal 401/403 during performSync above also halts backup
            // uploads in the same tick.
            if (cloudBackupRunner && syncEnabled) {
                try { await cloudBackupRunner(); } catch { /* best-effort */ }
            }
        }
        // Do NOT re-arm if the loop was stopped while this tick was running
        // (401/403 handling calls stopPeriodicSync mid-await).
        if (periodicStopped) return;
        syncTimeout = setTimeout(doSync, 30000); // 30 seconds
    };

    syncTimeout = setTimeout(doSync, 30000);

    // Sync when coming back online — save reference so we can remove it later
    onlineListener = () => performSync(token);
    window.addEventListener('online', onlineListener);

    // Sync when page becomes visible again (user returned from another app).
    // Reset the timer so we don't immediately re-sync on the next 30s tick.
    visibilityListener = () => {
        if (!document.hidden) {
            performSync(token);
            if (syncTimeout) {
                clearTimeout(syncTimeout);
                syncTimeout = setTimeout(doSync, 30000);
            }
        }
    };
    document.addEventListener('visibilitychange', visibilityListener);
}

/**
 * Stop periodic sync and clean up event listeners
 */
export function stopPeriodicSync() {
    periodicStopped = true;
    if (syncTimeout) {
        clearTimeout(syncTimeout);
        syncTimeout = null;
    }
    if (onlineListener) {
        window.removeEventListener('online', onlineListener);
        onlineListener = null;
    }
    if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
        visibilityListener = null;
    }
}

/**
 * Perform full sync with server
 */
export async function performSync(token) {
    // navigator.onLine is deliberately NOT consulted: Chrome misreports
    // offline on working networks (sleep/wake, virtual adapters — field
    // incident 2026-07-20 killed sync silently). Truly-offline fetches fail
    // fast below and the next tick retries.
    if (!syncEnabled || syncInProgress || !token) return;

    syncInProgress = true;
    let resolve;
    activeSyncPromise = new Promise(r => { resolve = r; });

    try {
        let lastSync = await localDB.getLastSyncTime();

        // Force full sync when local state is stale (>7 days since last sync).
        // Returning devices with old data need a complete refresh from server.
        const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;
        if (lastSync) {
            const age = Date.now() - new Date(lastSync).getTime();
            if (age > STALE_THRESHOLD_MS) {
                await localDB.setLastSyncTime(null);
                lastSync = null;
            }
        }

        // Snapshot pending changes before sending. Records their id + updated_at
        // so we can safely delete only these after sync, preserving any new changes
        // written by the user during the async network request.
        //
        // Filter by current user_id (from JWT sub): pending_changes has no
        // user_id index, so cross-account residue from a previous sign-in on
        // the same device would otherwise be pushed to the server under this
        // user's identity (and then be rejected by the backend L2.5 check,
        // generating noisy error logs). See plan twinkly-jingling-pretzel.md.
        const currentUserId = getUserIdFromJWT(token);
        const pendingChanges = await localDB.getPendingChanges(currentUserId || undefined);
        const pendingSnapshot = pendingChanges.map(c => ({ id: c.id, updated_at: c.updated_at }));

        // Organize pending changes by store type
        const changes = {
            books: [],
            highlights: [],
            notes: [],
            bookmarks: [],
            progress: [],
        };

        for (const change of pendingChanges) {
            if (changes[change.store]) {
                changes[change.store].push(change.data);
            }
        }

        // Split changes into batches to stay under the server's 500-item-per-store limit.
        // Each batch is a full sync request; the first carries last_sync to pull server
        // changes, subsequent batches only push remaining local changes.
        const SYNC_BATCH_SIZE = 200;
        const storeNames = Object.keys(changes);
        const needsBatching = storeNames.some(s => changes[s].length > SYNC_BATCH_SIZE);
        const batches = [];
        if (needsBatching) {
            // Determine max number of batches needed across all stores
            const maxBatches = Math.max(1, ...storeNames.map(s => Math.ceil(changes[s].length / SYNC_BATCH_SIZE)));
            for (let i = 0; i < maxBatches; i++) {
                const batch = {};
                for (const s of storeNames) {
                    batch[s] = changes[s].slice(i * SYNC_BATCH_SIZE, (i + 1) * SYNC_BATCH_SIZE);
                }
                batches.push(batch);
            }
        } else {
            batches.push(changes);
        }

        let result = null;
        for (let bi = 0; bi < batches.length; bi++) {
            const body = JSON.stringify({
                // Only the first batch requests server changes; subsequent batches
                // just push remaining local data (use current server_time so the
                // server returns nothing new).
                last_sync: bi === 0 ? lastSync : result?.server_time || lastSync,
                changes: batches[bi],
            });
            const response = await fetch(`${API_BASE}/reader/sync`, {
                method: 'POST',
                headers: apiHeaders(token),
                body,
                // keepalive lets the request survive page unload (critical for iOS/iPad
                // where pagehide freezes the page and cancels non-keepalive fetches).
                // Skip for large payloads — keepalive has a 64 KB in-flight budget.
                // Use byte length (not char length) because CJK chars are 3 bytes in UTF-8.
                keepalive: new Blob([body]).size < 61440,
            });

            if (!response.ok) {
                if (response.status === 401) {
                    console.warn('[Sync] Unauthorized - token expired or invalid, stopping sync');
                    stopPeriodicSync();
                    try {
                        const data = await response.json().catch(() => ({}));
                        const msg = data.error || data.message || null;
                        if (onUnauthorizedCallback) onUnauthorizedCallback(msg);
                    } catch {
                        if (onUnauthorizedCallback) onUnauthorizedCallback();
                    }
                    return;
                }
                if (response.status === 403) {
                    // TERMINAL state: the server refuses sync for this account's
                    // plan scope (e.g. body { code: 'sync_not_available' } for a
                    // Lens-only subscription). This is NOT an auth failure and
                    // NOT transient, so:
                    //   - stop the 30s loop (no retry storm from old SPAs)
                    //   - disable sync (performSync + _emergencyProgressPush no-op)
                    //   - KEEP _pending rows — they upload if the user upgrades
                    //   - do NOT log out, do NOT surface red UI
                    //   - reportError at most once per enable cycle
                    const data = await response.json().catch(() => ({}));
                    console.warn('[Sync] 403 — sync not available for this plan scope, stopping sync');
                    stopPeriodicSync();
                    syncEnabled = false;
                    if (!sync403Reported) {
                        sync403Reported = true;
                        reportError('Sync disabled by server (403)', {
                            level: 'warn',
                            metadata: { source: 'syncService.performSync', code: data?.code || null },
                        });
                    }
                    return;
                }
                throw new Error(`Sync failed: ${response.status}`);
            }

            const batchResult = await response.json();
            // Keep the first batch's result (has full server changes); update server_time from last batch.
            if (bi === 0) {
                result = batchResult;
            } else {
                result.server_time = batchResult.server_time;
            }
        }

        const isFullSync = lastSync === null;
        const userId = getUserIdFromJWT(token);

        if (isFullSync) {
            const nBooks = result.changes?.books?.length ?? 0;
            const nProgress = result.changes?.progress?.length ?? 0;
            if (nBooks === 0 && nProgress === 0) {
                console.warn('[Sync] Backend returned empty full state. Ensure /reader/sync returns full user data when last_sync is null.');
            }
        }

        // Cache for merge — D1 doesn't store scrollTop/scrollHeight on progress
        let localProgressMap = new Map();
        // Merged progress rows written to IDB this sync — handed to subscribers
        // so an open reader can detect another device's newer position.
        let mergedProgress = [];

        // Full sync: clear user stores then apply server state.
        // SAFETY: preserve locally-pending items that the server may not have returned.
        // This list MUST match the stores cleared below — derived here so adding a
        // new clearUserStoreForReplace() call without updating pending protection
        // becomes an obvious oversight in the same block of code.
        if (isFullSync && userId) {
            // note_revisions is included in the full-replace set: revisions are
            // server-authoritative (trigger-written) and the client never holds
            // unsynced revision rows, so wiping + replacing is correct and
            // avoids accumulating orphan revisions for deleted notes.  Not in
            // `pendingStores` for the "preserve local pending" step because
            // there are no local-only revisions to preserve.
            const pendingStores = ['progress', 'highlights', 'notes', 'bookmarks'];
            const localPendingByStore = {};
            for (const store of pendingStores) {
                localPendingByStore[store] = await localDB.getPendingItems(store, userId);
            }

            // Cache ALL local progress before clearing — D1 doesn't store scrollTop/scrollHeight
            const allLocalProgress = await localDB.getAllProgress(userId);
            localProgressMap = new Map(
                (allLocalProgress || []).map(p => [`${p.user_id}:${p.book_id}`, p])
            );

            // Clear all stores listed in pendingStores — single source of truth.
            // Plus note_revisions (no preserve needed, see above).
            await Promise.all([
                ...pendingStores.map(s => localDB.clearUserStoreForReplace(s, userId)),
                localDB.clearUserStoreForReplace('note_revisions', userId),
            ]);

            // Apply server changes
            if (result.changes) {
                if (result.changes.highlights?.length > 0) {
                    await localDB.bulkPut('highlights', result.changes.highlights);
                }
                if (result.changes.notes?.length > 0) {
                    await localDB.bulkPut('notes', result.changes.notes);
                }
                if (result.changes.bookmarks?.length > 0) {
                    await localDB.bulkPut('bookmarks', result.changes.bookmarks);
                }
                if (result.changes.note_revisions?.length > 0) {
                    await localDB.bulkPut('note_revisions', result.changes.note_revisions);
                }
            }

            // Restore locally-pending items that the server didn't return.
            // For progress, compare by user_id:book_id (not id) because local
            // records use UUID ids while server returns composite ids.
            for (const store of pendingStores) {
                const serverItems = result.changes?.[store] || [];
                let isOrphaned;
                if (store === 'progress') {
                    const serverKeys = new Set(serverItems.map(i => `${i.user_id}:${i.book_id}`));
                    isOrphaned = (item) => !serverKeys.has(`${item.user_id}:${item.book_id}`) && !item.deleted_at;
                } else {
                    const serverIds = new Set(serverItems.map(i => i.id));
                    isOrphaned = (item) => !serverIds.has(item.id) && !item.deleted_at;
                }
                const orphaned = (localPendingByStore[store] || []).filter(isOrphaned);
                if (orphaned.length > 0) {
                    await localDB.bulkPut(store, orphaned);
                }
            }
        }

        // Apply server changes to local DB
        if (result.changes) {
            // --- Books (server always returns the COMPLETE list) ---
            if (result.changes.books !== undefined) {
                const localFileIds = await localDB.getLocalFileBookIds();
                const serverBookIds = new Set((result.changes.books || []).map((b) => b.id));

                if (userId) {
                    const keepIds = new Set([...serverBookIds, ...localFileIds]);
                    await localDB.removeBooksForUserExcept(userId, keepIds);
                }

                if (result.changes.books?.length > 0) {
                    const locallyDeletedIds = new Set();
                    for (const serverBook of result.changes.books) {
                        const localBook = await localDB.getBook(serverBook.id);
                        if (localBook?.deleted_at && !serverBook.deleted_at) {
                            locallyDeletedIds.add(serverBook.id);
                        }
                    }
                    const booksToApply = result.changes.books.filter(b => !locallyDeletedIds.has(b.id));
                    for (let i = 0; i < booksToApply.length; i++) {
                        const localBook = await localDB.getBook(booksToApply[i].id);

                        // P3 PDF pipeline: when receiving an epub_from_pdf row,
                        // check whether the local book_files cache holds the
                        // ORIGINAL PDF bytes from the user's upload. If so,
                        // evict — EpubReader passes book_files[id] to JSZip
                        // which throws "Can't find end of central directory"
                        // on PDF input. After eviction, getBookFile falls
                        // through to fetchBookFileFromCloud and downloads the
                        // converted EPUB at file_url. Magic-byte check (rather
                        // than blanket evict) avoids re-downloading when the
                        // EPUB is already cached correctly.
                        if (booksToApply[i].file_type === 'epub_from_pdf') {
                            try {
                                const cached = await localDB.getBookFile(booksToApply[i].id);
                                if (cached && cached.byteLength >= 4) {
                                    const head = new Uint8Array(cached, 0, 4);
                                    // PDF magic: %PDF (0x25 0x50 0x44 0x46)
                                    const looksLikePdf = head[0] === 0x25 && head[1] === 0x50 &&
                                                         head[2] === 0x44 && head[3] === 0x46;
                                    if (looksLikePdf) {
                                        await localDB.deleteBookFile(booksToApply[i].id);
                                    } else {
                                        // §F17: same-path overwrite case. Worker publishes
                                        // partial EPUBs incrementally to the same R2 key —
                                        // magic byte stays 'PK' (zip), file_url stays
                                        // identical, only epub_version bumps. Without this
                                        // check, reader keeps serving the partial-stage EPUB
                                        // long after the full conversion landed in R2.
                                        const cachedVer = await localDB.getBookFileEpubVersion(booksToApply[i].id);
                                        const serverVer = booksToApply[i].epub_version ?? 0;
                                        if (cachedVer != null && serverVer > cachedVer) {
                                            await localDB.deleteBookFile(booksToApply[i].id);
                                            console.log(`[Sync §F17] Evicted stale EPUB for ${booksToApply[i].id} (cached v${cachedVer}, server v${serverVer})`);
                                        }
                                    }
                                }
                            } catch (e) {
                                console.warn('[Sync] PDF-bytes evict check failed for', booksToApply[i].id, e);
                            }
                        }

                        // Curated book replace: if server rewires file_url (admin replaced
                        // the EPUB), the local blob is stale. Compare against the file_url
                        // PINNED ON THE BLOB ITSELF (book_files.file_url) — NOT against
                        // localBook.file_url. The books-store file_url may have already
                        // been overwritten by an earlier sync (before this eviction code
                        // was deployed, or if the merge below ran in a prior pass), making
                        // localBook.file_url === server.file_url even though the cached
                        // blob is from an older download. The pinned blob file_url only
                        // changes on actual re-download via fetchBookFileFromCloud, so it
                        // is the canonical source of truth for "what version is cached".
                        if (localBook && localBook.user_id === userId) {
                            try {
                                // Primary: file_url pinned on the blob itself (set on download).
                                // Fallback: localBook.file_url — only useful for blobs that
                                // were saved BEFORE this eviction code was deployed (no pinned
                                // value on the blob) AND whose books-store entry hasn't yet
                                // been overwritten by a sync that pulled the new file_url.
                                // After one full deploy + download cycle, only the primary
                                // path matters.
                                const downloadedFileUrl = await localDB.getBookFileSourceUrl(booksToApply[i].id);
                                const compareUrl = downloadedFileUrl ?? localBook.file_url ?? null;
                                const serverFileUrl = booksToApply[i].file_url;
                                if (compareUrl && serverFileUrl && compareUrl !== serverFileUrl) {
                                    await localDB.deleteBookFile(booksToApply[i].id);
                                    console.log(`[Sync] Evicted stale EPUB for ${booksToApply[i].id} (was ${compareUrl}, now ${serverFileUrl})`);
                                }
                            } catch (e) {
                                console.warn('[Sync] Stale-blob evict check failed for', booksToApply[i].id, e);
                            }
                        }

                        // Only inherit local-only fields when the local record
                        // belongs to the SAME user. If a prior session left an
                        // orphan record from another user (IDB keyPath=id, no
                        // user_id partition), inheriting its file_url would
                        // pollute the current user's data with another user's
                        // R2 path, causing a 403 on download. See Bug #2.
                        if (localBook && localBook.user_id === userId) {
                            // Validate local file_url ownership before inheriting.
                            // Even if user_id matches, the file_url itself may be
                            // polluted from a prior bug (another user's R2 prefix
                            // written into this user's record). Only inherit if
                            // the path belongs to the current user or is a curated
                            // book path. Otherwise let the server value win (which
                            // may be null — that's correct, the book has no cloud file).
                            const localFileUrl = localBook.file_url;
                            const isLocalFileUrlSafe = !localFileUrl
                                || (typeof localFileUrl === 'string' && (
                                    localFileUrl.startsWith(`${userId}/`)
                                    || localFileUrl.startsWith(`epub_from_pdf/${userId}/`)
                                    || localFileUrl.startsWith(`source-pdfs/${userId}/`)
                                    || localFileUrl.startsWith('curated-books/')
                                    || localFileUrl.startsWith('/reader/curated-books/')
                                ));
                            booksToApply[i] = {
                                ...booksToApply[i],
                                file_url: booksToApply[i].file_url ?? (isLocalFileUrlSafe ? localFileUrl : null),
                                language: booksToApply[i].language ?? localBook.language,
                                total_word_count: booksToApply[i].total_word_count ?? localBook.total_word_count,
                                language_confirmed_value: booksToApply[i].language_confirmed_value ?? localBook.language_confirmed_value,
                                is_guide_book: booksToApply[i].is_guide_book ?? localBook.is_guide_book,
                            };
                        }
                        // Infer is_guide_book from canonical_book_id for ALL
                        // books, even when localBook is absent (e.g. after
                        // logout + re-login clears the books store). D1 does
                        // not store is_guide_book, but canonical_book_id IS
                        // synced and guide books have a deterministic prefix.
                        if (!booksToApply[i].is_guide_book) {
                            booksToApply[i].is_guide_book =
                                booksToApply[i].canonical_book_id?.startsWith('ev_guide_book_v1_') || false;
                        }
                    }
                    await localDB.bulkPut('books', booksToApply);

                    for (const id of localFileIds) {
                        if (!serverBookIds.has(id)) {
                            const book = await localDB.getBook(id);
                            if (book && !book.deleted_at) await localDB.bulkPut('books', [book]);
                        }
                    }
                }
            }
            // --- Highlights / Notes / Bookmarks / Revisions (already applied above for full sync) ---
            if (!isFullSync) {
                if (result.changes.highlights?.length > 0) {
                    await localDB.bulkPut('highlights', result.changes.highlights);
                }
                if (result.changes.notes?.length > 0) {
                    await localDB.bulkPut('notes', result.changes.notes);
                }
                if (result.changes.bookmarks?.length > 0) {
                    await localDB.bulkPut('bookmarks', result.changes.bookmarks);
                }
                if (result.changes.note_revisions?.length > 0) {
                    await localDB.bulkPut('note_revisions', result.changes.note_revisions);
                }
            }
            // --- Progress (merge to preserve scrollTop/scrollHeight not stored in D1) ---
            if (result.changes.progress?.length > 0) {
                if (!isFullSync) {
                    for (const prog of result.changes.progress) {
                        const local = await localDB.getProgress(prog.book_id, prog.user_id);
                        if (local) localProgressMap.set(`${prog.user_id}:${prog.book_id}`, local);
                    }
                }
                mergedProgress = mergeProgressWithLocal(result.changes.progress, localProgressMap);
                await localDB.bulkPut('progress', mergedProgress);
            }
        }

        // Only clear the pending changes that were part of this sync batch.
        // Changes written by the user during the network request are preserved.
        await localDB.clearPendingChangesBatch(pendingSnapshot);

        await localDB.setLastSyncTime(result.server_time);

        if (syncCompleteCallbacks.size > 0) {
            const payload = { progress: mergedProgress };
            for (const cb of syncCompleteCallbacks) {
                try { cb(payload); } catch (e) { console.warn('[Sync] onComplete error:', e); }
            }
        }

    } catch (error) {
        if (error && !error.message?.includes('401') && !error.message?.includes('Unauthorized')) {
            console.error('[Sync] Error:', error);
            reportError(error.message || 'Sync failed', { level: 'error', stack: error.stack, metadata: { source: 'syncService.performSync' } });
        }
    } finally {
        syncInProgress = false;
        resolve();
        activeSyncPromise = null;
    }
}

/**
 * Force immediate sync — waits for any in-flight sync to finish first.
 */
export async function forceSync(token) {
    if (activeSyncPromise) {
        await activeSyncPromise;
    }
    await performSync(token);
}

/**
 * Clear local data for logout or account switch.
 *
 * IMPORTANT ordering: this disables `syncEnabled` as its first step, so any
 * subsequent call to `performSync` / `forceSync` will be a no-op. Callers
 * that want a final sync before wiping MUST run `forceSync(token)` first,
 * then `clearAllData()`.
 *
 * @param {{ userId?: string | null }} [opts]
 *   - userId present → delete only this user's records (safe, recommended)
 *   - userId absent  → wipe all user-scoped stores (blunt fallback for when
 *                      the caller doesn't know the user id)
 */
export async function clearAllData(opts = {}) {
    syncEnabled = false;
    stopPeriodicSync();
    if (opts && opts.userId) {
        await localDB.clearCurrentUserData(opts.userId);
    } else {
        await localDB.clearUserData();
    }
}

export { mergeProgressWithLocal, mergeProgressWithLocal as _test_mergeProgressWithLocal };
